import { ChannelType, defaultEnterSendsMessage } from './atendimentoEnums';

export const DEFAULT_BOT_REPLY_SPLIT_MAX_CHARACTERS = 320;

export const BOT_REPLY_SPLIT_MIN_CHARACTERS = 80;

export const BOT_REPLY_SPLIT_MAX_CHARACTERS_LIMIT = 4000;

export interface InboxBotReplySplitSettings {
  enabled?: boolean;
  maxCharacters?: number;
}

export function resolveBotReplySplitMaxCharacters(settings?: InboxBotReplySplitSettings): number {
  const value = settings?.maxCharacters;
  if (typeof value === 'number' && value > 0)
  {
    return Math.min(
      Math.max(value, BOT_REPLY_SPLIT_MIN_CHARACTERS),
      BOT_REPLY_SPLIT_MAX_CHARACTERS_LIMIT
    );
  }

  return DEFAULT_BOT_REPLY_SPLIT_MAX_CHARACTERS;
}

export interface InboxConfigurationBase {
  enterSendsMessage?: boolean;
  botReplySplit?: InboxBotReplySplitSettings;
}

export interface WidgetPreChatField {
  name: string;
  label: string;
  type: string;
  placeholder?: string;
  enabled: boolean;
  required: boolean;
}

export interface WebchatInboxConfiguration extends InboxConfigurationBase {
  widgetToken?: string;
  headerTitle?: string;
  welcomeMessage?: string;
  launcherLabel?: string;
  primaryColor?: string;
  position?: string;
  launcherCloseBehavior?: 'minimize' | 'resolve';
  voiceEnabled?: boolean;
  preChatFormEnabled?: boolean;
  preChatMessage?: string;
  preChatFields?: WidgetPreChatField[];
  emailCollectEnabled?: boolean;
  emailCollectMessage?: string;
  hmacToken?: string;
  hasHmacToken?: boolean;
  hmacMandatory?: boolean;
  visitorTokenSecret?: string;
  hasVisitorTokenSecret?: boolean;
  allowedDomains?: string[];
  hasAvatar?: boolean;
  businessHours?: InboxBusinessHours;
}

export interface InboxBusinessHoursWindow {
  days?: number[];
  start?: string;
  end?: string;
}

export interface InboxBusinessHours {
  enabled?: boolean;
  timeZoneId?: string | null;
  windows?: InboxBusinessHoursWindow[];
}

export const DEFAULT_INBOX_BUSINESS_HOURS: InboxBusinessHours = {
  enabled: true,
  windows: [{ days: [1, 2, 3, 4, 5], start: '08:00', end: '18:00' }],
};

export const BUSINESS_HOUR_WEEKDAYS: { id: number; label: string; name: string }[] = [
  { id: 1, label: 'Seg', name: 'segunda' },
  { id: 2, label: 'Ter', name: 'terça' },
  { id: 3, label: 'Qua', name: 'quarta' },
  { id: 4, label: 'Qui', name: 'quinta' },
  { id: 5, label: 'Sex', name: 'sexta' },
  { id: 6, label: 'Sáb', name: 'sábado' },
  { id: 0, label: 'Dom', name: 'domingo' },
];

export function getInboxBusinessHoursWindow(
  hours?: InboxBusinessHours | null
): { days: number[]; start: string; end: string } {
  const hoursWindow = hours?.windows?.[0];
  const days = hoursWindow?.days;
  return {
    days: Array.isArray(days) && days.length > 0 ? days : [1, 2, 3, 4, 5],
    start: hoursWindow?.start || '08:00',
    end: hoursWindow?.end || '18:00',
  };
}

export function formatInboxBusinessHoursSummary(hours?: InboxBusinessHours | null): string {
  const window = getInboxBusinessHoursWindow(hours);
  const selected = BUSINESS_HOUR_WEEKDAYS.filter(day => window.days.includes(day.id));
  if (selected.length === 0)
  {
    return '';
  }

  const names = selected.map(day => day.name);
  let daysLabel = names[0];
  if (selected.length === 2)
  {
    daysLabel = `${names[0]} e ${names[1]}`;
  }
  else if (selected.length > 2)
  {
    const ids = selected.map(day => day.id);
    const isWeekdays =
      ids.length === 5
      && [1, 2, 3, 4, 5].every(id => ids.includes(id));
    daysLabel = isWeekdays
      ? 'segunda a sexta'
      : `${names.slice(0, -1).join(', ')} e ${names[names.length - 1]}`;
  }

  const start = formatClockLabel(window.start);
  const end = formatClockLabel(window.end);
  return `${daysLabel}, das ${start} às ${end}`;
}

function formatClockLabel(value: string): string {
  const [hours, minutes] = value.split(':');
  const hourNumber = Number(hours);
  if (Number.isNaN(hourNumber))
  {
    return value;
  }

  if (!minutes || minutes === '00')
  {
    return `${hourNumber}h`;
  }

  return `${hourNumber}h${minutes}`;
}

export function formatInboxBusinessHoursTimeZoneLabel(timeZoneId?: string | null): string {
  if (!timeZoneId || !timeZoneId.trim())
  {
    return 'horário de Brasília';
  }

  const id = timeZoneId.trim().toLowerCase();
  if (
    id === 'america/sao_paulo'
    || id === 'e. south america standard time'
  )
  {
    return 'horário de Brasília';
  }

  if (
    id === 'america/manaus'
    || id === 'sa western standard time'
  )
  {
    return 'horário de Manaus';
  }

  if (id === 'america/noronha')
  {
    return 'horário de Fernando de Noronha';
  }

  return 'horário da equipe';
}

export interface WhatsAppInboxConfiguration extends InboxConfigurationBase {
  phoneNumberId?: string;
  accessToken?: string;
  hasAccessToken?: boolean;
  verifyToken?: string;
  appSecret?: string;
  hasAppSecret?: boolean;
  businessAccountId?: string;
  phoneNumber?: string;
}

export interface InstagramInboxConfiguration extends InboxConfigurationBase {
  instagramAccountId?: string;
  pageAccessToken?: string;
  hasPageAccessToken?: boolean;
  verifyToken?: string;
  appSecret?: string;
  hasAppSecret?: boolean;
}

export interface MessengerInboxConfiguration extends InboxConfigurationBase {
  pageId?: string;
  pageAccessToken?: string;
  hasPageAccessToken?: boolean;
  verifyToken?: string;
  appSecret?: string;
  hasAppSecret?: boolean;
}

export interface EmailInboxConfiguration extends InboxConfigurationBase {
  imapHost?: string;
  imapPort?: number;
  imapUseSsl?: boolean;
  imapUsername?: string;
  imapPassword?: string;
  hasImapPassword?: boolean;
  fromAddress?: string;
  fromName?: string;
}

export type InboxChannelConfiguration =
  | WebchatInboxConfiguration
  | WhatsAppInboxConfiguration
  | InstagramInboxConfiguration
  | MessengerInboxConfiguration
  | EmailInboxConfiguration;

export const KEEP_EXISTING_SECRET_HINT = 'Deixe em branco para manter o valor atual.';

export function parseInboxConfiguration<T>(json: string | undefined | null): T {
  if (!json || json === '{}') {
    return {} as T;
  }

  try {
    return JSON.parse(json) as T;
  }
  catch {
    return {} as T;
  }
}

export function serializeInboxConfiguration(config: object): string {
  return JSON.stringify(config);
}

export function resolveApiRootUrl(apiUrl?: string): string {
  const raw = (apiUrl || process.env.REACT_APP_API_URL || window.location.origin).replace(/\/$/, '');
  return raw.replace(/\/api$/i, '');
}

export function buildWidgetTestPageUrl(apiRootUrl: string, widgetToken: string): string {
  const base = resolveApiRootUrl(apiRootUrl).replace(/\/$/, '');
  return `${base}/widget-test.html?token=${encodeURIComponent(widgetToken)}`;
}

export function buildWidgetAvatarUrl(
  apiRootUrl: string,
  widgetToken: string,
  cacheBust?: string
): string {
  const base = resolveApiRootUrl(apiRootUrl).replace(/\/$/, '');
  const url = `${base}/api/public/widget/${encodeURIComponent(widgetToken)}/avatar`;
  if (!cacheBust)
  {
    return url;
  }

  return `${url}?v=${encodeURIComponent(cacheBust)}`;
}

const WIDGET_AVATAR_MAX_BYTES = 5 * 1024 * 1024;
const WIDGET_AVATAR_ALLOWED_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];

export function validateWidgetAvatarFile(file: File): string | undefined {
  if (!WIDGET_AVATAR_ALLOWED_TYPES.includes(file.type))
  {
    return 'A imagem deve ser JPG, PNG, GIF ou WEBP.';
  }

  if (file.size > WIDGET_AVATAR_MAX_BYTES)
  {
    return 'A imagem deve ter no máximo 5 MB.';
  }

  return undefined;
}

export function buildEmbedScript(apiBaseUrl: string, widgetToken: string): string {
  const base = resolveApiRootUrl(apiBaseUrl).replace(/\/$/, '');
  return `<script>
  (function(w,d,s,o,f,js,fjs){
    w.KnowWidget=w.KnowWidget||{init:function(o){this._pending=Object.assign({},this._pending,o||{});}};
    js=d.createElement(s);js.async=1;js.src="${base}/api/public/widget/${widgetToken}/embed.js";
    fjs=d.getElementsByTagName(s)[0];fjs.parentNode.insertBefore(js,fjs);
  })(window,document,'script');
</script>`;
}

export function buildKnowWidgetInitExample(): string {
  return `<script>
  KnowWidget.init({
    identifier: 'user-123',
    identifierHash: '<assinatura HMAC gerada no seu backend>',
    name: 'João Silva',
    email: 'fulano@empresa.com',
    phone: '+5511999990000'
  });
</script>`;
}

export const DEFAULT_PRE_CHAT_MESSAGE = 'Antes de começar, como podemos te chamar?';

export function getDefaultPreChatFields(): WidgetPreChatField[] {
  return [
    {
      name: 'fullName',
      label: 'Nome',
      type: 'text',
      placeholder: 'Seu nome',
      enabled: true,
      required: true,
    },
    {
      name: 'email',
      label: 'E-mail',
      type: 'email',
      placeholder: 'Seu e-mail',
      enabled: true,
      required: false,
    },
    {
      name: 'phoneNumber',
      label: 'Telefone',
      type: 'tel',
      placeholder: 'Seu telefone',
      enabled: false,
      required: false,
    },
  ];
}

export const DEFAULT_LAUNCHER_LABEL = 'Como podemos te ajudar?';

export function getDefaultConfiguration(channelType: ChannelType): InboxChannelConfiguration {
  if (channelType === ChannelType.WebWidget) {
    return {
      headerTitle: 'Atendimento',
      welcomeMessage: 'Olá! Como podemos ajudar?',
      launcherLabel: DEFAULT_LAUNCHER_LABEL,
      primaryColor: '#2563eb',
      position: 'right',
      voiceEnabled: true,
      enterSendsMessage: defaultEnterSendsMessage(channelType),
      preChatFormEnabled: false,
      preChatMessage: DEFAULT_PRE_CHAT_MESSAGE,
      preChatFields: getDefaultPreChatFields(),
      emailCollectEnabled: false,
      emailCollectMessage: 'Para darmos continuidade, informe seu e-mail:',
      hmacMandatory: false,
      allowedDomains: [],
    };
  }

  if (channelType === ChannelType.Email) {
    return {
      imapPort: 993,
      imapUseSsl: true,
      enterSendsMessage: defaultEnterSendsMessage(channelType),
    };
  }

  return {
    enterSendsMessage: defaultEnterSendsMessage(channelType),
  };
}
